import React, { useState } from 'react';
import {
  Activity,
  Bug,
  Zap,
  ExternalLink,
  Terminal,
  Copy,
  Check,
  FileCode,
  ShieldCheck,
  RefreshCw,
  Cpu,
  Globe,
  AlertTriangle,
  Code2,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { Sentry } from '../../../sentry.js';
import { useNotificationStore } from '../../notifications/store/useNotificationStore.js';

interface TestLogItem {
  id: string;
  timestamp: string;
  source: 'frontend' | 'backend';
  errorType: string;
  message: string;
  status: 'triggered' | 'failed';
  sentryQuery: string;
}

export const SentryDiagnosticPanel: React.FC = () => {
  const { showToast } = useNotificationStore();
  const [activeTab, setActiveTab] = useState<'all' | 'frontend' | 'backend'>('all');
  const [logs, setLogs] = useState<TestLogItem[]>([]);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [loadingType, setLoadingType] = useState<string | null>(null);

  const addLog = (
    source: 'frontend' | 'backend',
    errorType: string,
    message: string,
    status: 'triggered' | 'failed'
  ) => {
    const newItem: TestLogItem = {
      id: `${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
      timestamp: new Date().toLocaleTimeString('id-ID', { hour12: false }),
      source,
      errorType,
      message,
      status,
      sentryQuery: `"${errorType}"`,
    };
    setLogs((prev) => [newItem, ...prev.slice(0, 24)]);
  };

  const copyQuery = (query: string, id: string) => {
    navigator.clipboard.writeText(query);
    setCopiedId(id);
    showToast('Query Dicoppy', 'Filter pencarian Sentry disalin ke clipboard.', 'success');
    setTimeout(() => setCopiedId(null), 2000);
  };

  // ==========================================
  // FRONTEND ERROR TRIGGERS (Source Map Tests)
  // ==========================================

  const triggerFrontendSyntaxError = () => {
    setLoadingType('fe-syntax');
    try {
      // Simulates JSON parse syntax error inside a named function
      function parseCorruptedUibnManifest() {
        return JSON.parse('{"uibn": "catalog", "version": 2, invalid_syntax_here}');
      }
      parseCorruptedUibnManifest();
    } catch (err: any) {
      Sentry.captureException(err, {
        tags: { test_suite: 'frontend_source_maps', error_class: 'SyntaxError' },
      });
      addLog('frontend', 'SyntaxError', err.message, 'triggered');
      showToast('SyntaxError Frontend Dipicu', 'Exception berhasil ditangkap React SDK dan dikirim ke Sentry dengan Source Maps.', 'warning');
    } finally {
      setLoadingType(null);
    }
  };

  const triggerFrontendReferenceError = () => {
    setLoadingType('fe-reference');
    try {
      function processUibnVerificationWorkflow() {
        // Accessing undefined variable in scope
        return (window as any).nonExistentUibnVerificationProcessor.execute();
      }
      processUibnVerificationWorkflow();
    } catch (err: any) {
      Sentry.captureException(err, {
        tags: { test_suite: 'frontend_source_maps', error_class: 'ReferenceError' },
      });
      addLog('frontend', 'ReferenceError', err.message, 'triggered');
      showToast('ReferenceError Frontend Dipicu', 'Pengecualian variabel tidak terdefinisi dikirim ke Sentry.', 'warning');
    } finally {
      setLoadingType(null);
    }
  };

  const triggerFrontendTypeError = () => {
    setLoadingType('fe-type');
    try {
      function renderUibnDigitalCertificate() {
        const certificateData: any = null;
        return certificateData.calculateHashSignature();
      }
      renderUibnDigitalCertificate();
    } catch (err: any) {
      Sentry.captureException(err, {
        tags: { test_suite: 'frontend_source_maps', error_class: 'TypeError' },
      });
      addLog('frontend', 'TypeError', err.message, 'triggered');
      showToast('TypeError Frontend Dipicu', 'Pengecualian tipe objek null/undefined dikirim ke Sentry.', 'warning');
    } finally {
      setLoadingType(null);
    }
  };

  const triggerFrontendNetworkError = async () => {
    setLoadingType('fe-network');
    try {
      await fetch('https://invalid-subdomain-uibn-diagnostic.domain.invalid/api/sync', {
        method: 'POST',
      });
    } catch (err: any) {
      Sentry.captureException(new Error(`NetworkError Frontend Test: ${err.message || 'Failed to fetch'}`), {
        tags: { test_suite: 'frontend_source_maps', error_class: 'NetworkError' },
      });
      addLog('frontend', 'NetworkError', err.message || 'Failed to fetch', 'triggered');
      showToast('NetworkError Frontend Dipicu', 'Gagal koneksi jaringan berhasil ditangkap & dikirim ke Sentry.', 'warning');
    } finally {
      setLoadingType(null);
    }
  };

  const triggerFrontendRangeError = () => {
    setLoadingType('fe-range');
    try {
      function allocateUibnCatalogMemory() {
        return new Array(-1);
      }
      allocateUibnCatalogMemory();
    } catch (err: any) {
      Sentry.captureException(err, {
        tags: { test_suite: 'frontend_source_maps', error_class: 'RangeError' },
      });
      addLog('frontend', 'RangeError', err.message, 'triggered');
      showToast('RangeError Frontend Dipicu', 'Pengecualian batas memori/array dikirim ke Sentry.', 'warning');
    } finally {
      setLoadingType(null);
    }
  };

  const triggerFrontendPromiseRejection = () => {
    setLoadingType('fe-promise');
    const rejectionErr = new Error('UIBN Unhandled Promise Rejection Test: Async Verification Timeout');
    Promise.reject(rejectionErr);
    Sentry.captureException(rejectionErr, {
      tags: { test_suite: 'frontend_source_maps', error_class: 'PromiseRejection' },
    });
    addLog('frontend', 'PromiseRejection', rejectionErr.message, 'triggered');
    showToast('Unhandled Promise Rejection Dipicu', 'Janji asinkron ditolak & dikirim ke Sentry.', 'warning');
    setLoadingType(null);
  };

  // ==========================================
  // BACKEND ERROR TRIGGERS (Cloudflare Worker)
  // ==========================================

  const triggerBackendError = async (endpoint: string, typeName: string) => {
    setLoadingType(`be-${typeName}`);
    try {
      const res = await fetch(endpoint);
      if (res.status === 500) {
        addLog('backend', typeName, `HTTP 500 Internal Error dari ${endpoint}`, 'triggered');
        showToast(
          `Backend ${typeName} Dipicu`,
          `Rute ${endpoint} melempar exception 500 di Hono Cloudflare Worker. Cek Dasbor Sentry.`,
          'info'
        );
      } else {
        addLog('backend', typeName, `HTTP ${res.status} dari ${endpoint}`, 'triggered');
        showToast(`Backend ${typeName} Diakses`, `Status HTTP: ${res.status}`, 'info');
      }
    } catch (err: any) {
      addLog('backend', typeName, `Network exception: ${err.message}`, 'failed');
      showToast('Gagal Memanggil Backend', err.message, 'error');
    } finally {
      setLoadingType(null);
    }
  };

  const triggerAsyncWaitUntilError = async () => {
    setLoadingType('be-async-waituntil');
    try {
      const res = await fetch('/api/debug-sentry/async-waituntil');
      const data = await res.json().catch(() => ({}));
      addLog('backend', 'AsyncWaitUntil', `HTTP ${res.status}: ${data?.message || 'WaitUntil dipicu'}`, 'triggered');
      showToast(
        'Async WaitUntil Worker Dipicu',
        'Respons 200 OK diterima langsung. Exception latar belakang sedang dikirim ke Sentry via c.executionCtx.waitUntil.',
        'success'
      );
    } catch (err: any) {
      addLog('backend', 'AsyncWaitUntil', `Network exception: ${err.message}`, 'failed');
      showToast('Gagal Memanggil Backend', err.message, 'error');
    } finally {
      setLoadingType(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Panel */}
      <div className="bg-slate-900 text-white rounded-2xl p-6 shadow-md border border-slate-800 space-y-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-800 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-purple-500/20 text-purple-400 border border-purple-500/30 flex items-center justify-center shrink-0">
              <Cpu className="w-6 h-6 animate-pulse" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold text-white">
                  Panel Diagnostik Sentry & Source Maps
                </h2>
                <span className="px-2.5 py-0.5 rounded-full bg-purple-500/20 border border-purple-500/40 text-purple-300 text-[10px] font-extrabold uppercase">
                  Org: uibn
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-0.5">
                Uji verifikasi pemetaan stack trace TypeScript asli pada lingkungan Frontend & Backend Cloudflare Workers.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <a
              href="https://sentry.io/organizations/uibn/issues/"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-bold text-xs flex items-center gap-2 transition shadow-lg shadow-purple-900/30 cursor-pointer"
            >
              <span>Buka Dasbor Sentry.io</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>

        {/* Telemetry Status Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-1">
          <div className="bg-slate-800/80 border border-slate-700/60 rounded-xl p-3.5">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Frontend React SDK</span>
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
            </div>
            <div className="text-xs font-bold text-emerald-400 flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4" />
              <span>Session Replay & Tracing</span>
            </div>
          </div>

          <div className="bg-slate-800/80 border border-slate-700/60 rounded-xl p-3.5">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Backend Worker</span>
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
            </div>
            <div className="text-xs font-bold text-emerald-400 flex items-center gap-1.5">
              <Globe className="w-4 h-4" />
              <span>Hono Cloudflare Middleware</span>
            </div>
          </div>

          <div className="bg-slate-800/80 border border-slate-700/60 rounded-xl p-3.5">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Source Maps Upload</span>
              <span className="px-1.5 py-0.5 rounded bg-indigo-500/20 text-indigo-300 text-[9px] font-mono">@sentry/vite-plugin</span>
            </div>
            <div className="text-xs font-bold text-indigo-300 flex items-center gap-1.5">
              <FileCode className="w-4 h-4" />
              <span>build.sourcemap = true</span>
            </div>
          </div>

          <div className="bg-slate-800/80 border border-slate-700/60 rounded-xl p-3.5">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Proyek Sentry</span>
              <span className="text-[10px] text-slate-400 font-mono">uibn</span>
            </div>
            <div className="text-xs font-bold text-amber-300 flex items-center gap-1.5 truncate">
              <Code2 className="w-4 h-4 shrink-0" />
              <span className="truncate">de2d9f55...4512172302270544</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Grid: Triggers on Left, Execution Console on Right */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column: Test Suites (7 cols) */}
        <div className="lg:col-span-7 space-y-6">
          {/* Frontend Source Maps Test Suite */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-2xs space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold text-xs">
                  FE
                </div>
                <div>
                  <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                    Uji Source Maps Frontend (Browser React SDK)
                  </h3>
                  <p className="text-[11px] text-slate-500">
                    Memicu exception langsung di React untuk memverifikasi pemetaan file <code>.tsx</code>
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* SyntaxError */}
              <button
                type="button"
                onClick={triggerFrontendSyntaxError}
                disabled={loadingType === 'fe-syntax'}
                className="p-3 rounded-xl border border-slate-200 hover:border-indigo-300 bg-slate-50 hover:bg-indigo-50/50 text-left transition cursor-pointer group disabled:opacity-50"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold text-slate-800 group-hover:text-indigo-700">SyntaxError</span>
                  {loadingType === 'fe-syntax' ? <RefreshCw className="w-3.5 h-3.5 animate-spin text-indigo-600" /> : <Bug className="w-3.5 h-3.5 text-slate-400 group-hover:text-indigo-600" />}
                </div>
                <p className="text-[10px] text-slate-500 line-clamp-2">
                  Memparsa JSON tidak valid di dalam fungsi <code>parseCorruptedUibnManifest()</code>.
                </p>
              </button>

              {/* ReferenceError */}
              <button
                type="button"
                onClick={triggerFrontendReferenceError}
                disabled={loadingType === 'fe-reference'}
                className="p-3 rounded-xl border border-slate-200 hover:border-indigo-300 bg-slate-50 hover:bg-indigo-50/50 text-left transition cursor-pointer group disabled:opacity-50"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold text-slate-800 group-hover:text-indigo-700">ReferenceError</span>
                  {loadingType === 'fe-reference' ? <RefreshCw className="w-3.5 h-3.5 animate-spin text-indigo-600" /> : <Code2 className="w-3.5 h-3.5 text-slate-400 group-hover:text-indigo-600" />}
                </div>
                <p className="text-[10px] text-slate-500 line-clamp-2">
                  Mengakses variabel tak terdefinisi di <code>processUibnVerificationWorkflow()</code>.
                </p>
              </button>

              {/* TypeError */}
              <button
                type="button"
                onClick={triggerFrontendTypeError}
                disabled={loadingType === 'fe-type'}
                className="p-3 rounded-xl border border-slate-200 hover:border-indigo-300 bg-slate-50 hover:bg-indigo-50/50 text-left transition cursor-pointer group disabled:opacity-50"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold text-slate-800 group-hover:text-indigo-700">TypeError</span>
                  {loadingType === 'fe-type' ? <RefreshCw className="w-3.5 h-3.5 animate-spin text-indigo-600" /> : <AlertTriangle className="w-3.5 h-3.5 text-slate-400 group-hover:text-indigo-600" />}
                </div>
                <p className="text-[10px] text-slate-500 line-clamp-2">
                  Mempraktekkan pemanggilan method pada objek <code>null</code>.
                </p>
              </button>

              {/* NetworkError */}
              <button
                type="button"
                onClick={triggerFrontendNetworkError}
                disabled={loadingType === 'fe-network'}
                className="p-3 rounded-xl border border-slate-200 hover:border-indigo-300 bg-slate-50 hover:bg-indigo-50/50 text-left transition cursor-pointer group disabled:opacity-50"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold text-slate-800 group-hover:text-indigo-700">NetworkError</span>
                  {loadingType === 'fe-network' ? <RefreshCw className="w-3.5 h-3.5 animate-spin text-indigo-600" /> : <Zap className="w-3.5 h-3.5 text-slate-400 group-hover:text-indigo-600" />}
                </div>
                <p className="text-[10px] text-slate-500 line-clamp-2">
                  Melakukan HTTP request ke domain fiktif untuk memicu kegagalan jaringan.
                </p>
              </button>

              {/* RangeError */}
              <button
                type="button"
                onClick={triggerFrontendRangeError}
                disabled={loadingType === 'fe-range'}
                className="p-3 rounded-xl border border-slate-200 hover:border-indigo-300 bg-slate-50 hover:bg-indigo-50/50 text-left transition cursor-pointer group disabled:opacity-50"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold text-slate-800 group-hover:text-indigo-700">RangeError</span>
                  {loadingType === 'fe-range' ? <RefreshCw className="w-3.5 h-3.5 animate-spin text-indigo-600" /> : <Activity className="w-3.5 h-3.5 text-slate-400 group-hover:text-indigo-600" />}
                </div>
                <p className="text-[10px] text-slate-500 line-clamp-2">
                  Mengalokasikan array bernilai negatif <code>new Array(-1)</code>.
                </p>
              </button>

              {/* PromiseRejection */}
              <button
                type="button"
                onClick={triggerFrontendPromiseRejection}
                disabled={loadingType === 'fe-promise'}
                className="p-3 rounded-xl border border-slate-200 hover:border-indigo-300 bg-slate-50 hover:bg-indigo-50/50 text-left transition cursor-pointer group disabled:opacity-50"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold text-slate-800 group-hover:text-indigo-700">PromiseRejection</span>
                  {loadingType === 'fe-promise' ? <RefreshCw className="w-3.5 h-3.5 animate-spin text-indigo-600" /> : <FileCode className="w-3.5 h-3.5 text-slate-400 group-hover:text-indigo-600" />}
                </div>
                <p className="text-[10px] text-slate-500 line-clamp-2">
                  Menolak promise asinkron tanpa penangkap error (unhandled rejection).
                </p>
              </button>
            </div>
          </div>

          {/* Backend Source Maps Test Suite */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-2xs space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center font-bold text-xs">
                  BE
                </div>
                <div>
                  <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                    Uji Source Maps Backend (Hono Cloudflare Worker)
                  </h3>
                  <p className="text-[11px] text-slate-500">
                    Memanggil rute API terisolasi untuk melempar error server pada file <code>backend/app.ts</code>
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Backend ReferenceError */}
              <button
                type="button"
                onClick={() => triggerBackendError('/api/debug-sentry/reference-error', 'ReferenceError')}
                disabled={loadingType === 'be-ReferenceError'}
                className="p-3 rounded-xl border border-slate-200 hover:border-purple-300 bg-slate-50 hover:bg-purple-50/50 text-left transition cursor-pointer group disabled:opacity-50"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold text-slate-800 group-hover:text-purple-700">Backend ReferenceError</span>
                  {loadingType === 'be-ReferenceError' ? <RefreshCw className="w-3.5 h-3.5 animate-spin text-purple-600" /> : <Zap className="w-3.5 h-3.5 text-slate-400 group-hover:text-purple-600" />}
                </div>
                <p className="text-[10px] text-slate-500 line-clamp-2">
                  GET <code>/api/debug-sentry/reference-error</code> (Variable 'uibnGlobalState' is not defined)
                </p>
              </button>

              {/* Backend TypeError */}
              <button
                type="button"
                onClick={() => triggerBackendError('/api/debug-sentry/type-error', 'TypeError')}
                disabled={loadingType === 'be-TypeError'}
                className="p-3 rounded-xl border border-slate-200 hover:border-purple-300 bg-slate-50 hover:bg-purple-50/50 text-left transition cursor-pointer group disabled:opacity-50"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold text-slate-800 group-hover:text-purple-700">Backend TypeError</span>
                  {loadingType === 'be-TypeError' ? <RefreshCw className="w-3.5 h-3.5 animate-spin text-purple-600" /> : <Bug className="w-3.5 h-3.5 text-slate-400 group-hover:text-purple-600" />}
                </div>
                <p className="text-[10px] text-slate-500 line-clamp-2">
                  GET <code>/api/debug-sentry/type-error</code> (Cannot read properties of undefined)
                </p>
              </button>

              {/* Backend SyntaxError */}
              <button
                type="button"
                onClick={() => triggerBackendError('/api/debug-sentry/syntax-error', 'SyntaxError')}
                disabled={loadingType === 'be-SyntaxError'}
                className="p-3 rounded-xl border border-slate-200 hover:border-purple-300 bg-slate-50 hover:bg-purple-50/50 text-left transition cursor-pointer group disabled:opacity-50"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold text-slate-800 group-hover:text-purple-700">Backend SyntaxError</span>
                  {loadingType === 'be-SyntaxError' ? <RefreshCw className="w-3.5 h-3.5 animate-spin text-purple-600" /> : <Code2 className="w-3.5 h-3.5 text-slate-400 group-hover:text-purple-600" />}
                </div>
                <p className="text-[10px] text-slate-500 line-clamp-2">
                  GET <code>/api/debug-sentry/syntax-error</code> (Unexpected token 'UIBN' in JSON)
                </p>
              </button>

              {/* Backend NetworkError */}
              <button
                type="button"
                onClick={() => triggerBackendError('/api/debug-sentry/network-error', 'NetworkError')}
                disabled={loadingType === 'be-NetworkError'}
                className="p-3 rounded-xl border border-slate-200 hover:border-purple-300 bg-slate-50 hover:bg-purple-50/50 text-left transition cursor-pointer group disabled:opacity-50"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold text-slate-800 group-hover:text-purple-700">Backend NetworkError</span>
                  {loadingType === 'be-NetworkError' ? <RefreshCw className="w-3.5 h-3.5 animate-spin text-purple-600" /> : <Globe className="w-3.5 h-3.5 text-slate-400 group-hover:text-purple-600" />}
                </div>
                <p className="text-[10px] text-slate-500 line-clamp-2">
                  GET <code>/api/debug-sentry/network-error</code> (Failed database TLS handshake)
                </p>
              </button>

              {/* Backend RangeError */}
              <button
                type="button"
                onClick={() => triggerBackendError('/api/debug-sentry/range-error', 'RangeError')}
                disabled={loadingType === 'be-RangeError'}
                className="p-3 rounded-xl border border-slate-200 hover:border-purple-300 bg-slate-50 hover:bg-purple-50/50 text-left transition cursor-pointer group disabled:opacity-50"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold text-slate-800 group-hover:text-purple-700">Backend RangeError</span>
                  {loadingType === 'be-RangeError' ? <RefreshCw className="w-3.5 h-3.5 animate-spin text-purple-600" /> : <Activity className="w-3.5 h-3.5 text-slate-400 group-hover:text-purple-600" />}
                </div>
                <p className="text-[10px] text-slate-500 line-clamp-2">
                  GET <code>/api/debug-sentry/range-error</code> (Maximum recursion call stack limit)
                </p>
              </button>

              {/* Backend Tagged Error */}
              <button
                type="button"
                onClick={() => triggerBackendError('/api/debug-sentry/custom-tagged', 'CustomTaggedError')}
                disabled={loadingType === 'be-CustomTaggedError'}
                className="p-3 rounded-xl border border-slate-200 hover:border-purple-300 bg-slate-50 hover:bg-purple-50/50 text-left transition cursor-pointer group disabled:opacity-50"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold text-slate-800 group-hover:text-purple-700">Backend Tagged Exception</span>
                  {loadingType === 'be-CustomTaggedError' ? <RefreshCw className="w-3.5 h-3.5 animate-spin text-purple-600" /> : <FileCode className="w-3.5 h-3.5 text-slate-400 group-hover:text-purple-600" />}
                </div>
                <p className="text-[10px] text-slate-500 line-clamp-2">
                  GET <code>/api/debug-sentry/custom-tagged</code> (With tag source_map_test=true)
                </p>
              </button>

              {/* Backend Async WaitUntil Exception */}
              <button
                type="button"
                onClick={triggerAsyncWaitUntilError}
                disabled={loadingType === 'be-async-waituntil'}
                className="p-3 rounded-xl border border-purple-300 bg-purple-50/80 hover:bg-purple-100/80 text-left transition cursor-pointer group disabled:opacity-50 sm:col-span-2 shadow-2xs"
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-bold text-purple-900 group-hover:text-purple-950">
                      Backend Async WaitUntil Worker Exception
                    </span>
                    <span className="px-1.5 py-0.2 rounded bg-purple-200 text-purple-800 text-[9px] font-extrabold uppercase">
                      After Response 200 OK
                    </span>
                  </div>
                  {loadingType === 'be-async-waituntil' ? (
                    <RefreshCw className="w-3.5 h-3.5 animate-spin text-purple-700" />
                  ) : (
                    <Zap className="w-3.5 h-3.5 text-purple-700" />
                  )}
                </div>
                <p className="text-[10px] text-purple-800 leading-relaxed">
                  GET <code>/api/debug-sentry/async-waituntil</code> – Mengirim HTTP 200 OK langsung, kemudian mengeksekusi pemrosesan log & mendaftarkan exception ke Sentry di latar belakang melalui <code>c.executionCtx.waitUntil</code>.
                </p>
              </button>
            </div>
          </div>
        </div>

        {/* Right Column: Console Log & Source Maps Verification Guide (5 cols) */}
        <div className="lg:col-span-5 space-y-6">
          {/* Real-time Diagnostic Console */}
          <div className="bg-slate-950 text-slate-200 border border-slate-800 rounded-2xl p-5 shadow-md flex flex-col space-y-3 font-mono">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div className="flex items-center gap-2">
                <Terminal className="w-4 h-4 text-purple-400" />
                <h3 className="text-xs font-bold text-white uppercase tracking-wider">
                  Konsol Diagnostik Real-time
                </h3>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setActiveTab('all')}
                  className={`px-2 py-0.5 rounded text-[10px] font-bold cursor-pointer ${activeTab === 'all' ? 'bg-purple-600 text-white' : 'text-slate-400 hover:bg-slate-800'}`}
                >
                  Semua ({logs.length})
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('frontend')}
                  className={`px-2 py-0.5 rounded text-[10px] font-bold cursor-pointer ${activeTab === 'frontend' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:bg-slate-800'}`}
                >
                  FE ({logs.filter(l => l.source === 'frontend').length})
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('backend')}
                  className={`px-2 py-0.5 rounded text-[10px] font-bold cursor-pointer ${activeTab === 'backend' ? 'bg-purple-600 text-white' : 'text-slate-400 hover:bg-slate-800'}`}
                >
                  BE ({logs.filter(l => l.source === 'backend').length})
                </button>
              </div>
            </div>

            {/* Log Stream */}
            <div className="min-h-[260px] max-h-[360px] overflow-y-auto space-y-2 pr-1 custom-scrollbar text-[11px] leading-relaxed">
              {logs.length === 0 ? (
                <div className="p-8 text-center text-slate-500 font-sans space-y-2">
                  <Activity className="w-8 h-8 text-slate-700 mx-auto animate-pulse" />
                  <p className="text-xs font-medium">Belum ada uji coba error yang dipicu.</p>
                  <p className="text-[10px] text-slate-600">Klik salah satu tombol uji coba di sebelah kiri untuk memicu exception ke Sentry.io.</p>
                </div>
              ) : (
                logs
                  .filter((item) => activeTab === 'all' || item.source === activeTab)
                  .map((log) => (
                    <div
                      key={log.id}
                      className="p-2.5 rounded-lg bg-slate-900 border border-slate-800/80 space-y-1 hover:border-slate-700 transition"
                    >
                      <div className="flex items-center justify-between text-[10px]">
                        <div className="flex items-center gap-1.5">
                          <span className="text-slate-500">[{log.timestamp}]</span>
                          <span
                            className={`px-1.5 py-0.2 rounded uppercase font-bold text-[9px] ${
                              log.source === 'frontend'
                                ? 'bg-indigo-950 text-indigo-300 border border-indigo-800/50'
                                : 'bg-purple-950 text-purple-300 border border-purple-800/50'
                            }`}
                          >
                            {log.source === 'frontend' ? 'FE React' : 'BE Worker'}
                          </span>
                          <span className="font-bold text-amber-300">{log.errorType}</span>
                        </div>
                        {log.status === 'triggered' ? (
                          <span className="text-emerald-400 flex items-center gap-1 text-[9px]">
                            <CheckCircle2 className="w-3 h-3" />
                            <span>Dikirim</span>
                          </span>
                        ) : (
                          <span className="text-rose-400 flex items-center gap-1 text-[9px]">
                            <XCircle className="w-3 h-3" />
                            <span>Gagal</span>
                          </span>
                        )}
                      </div>

                      <p className="text-slate-300 font-sans text-[11px] break-words">
                        {log.message}
                      </p>

                      <div className="flex items-center justify-between pt-1 border-t border-slate-800/60 text-[10px]">
                        <span className="text-slate-500 truncate">Filter: {log.sentryQuery}</span>
                        <button
                          type="button"
                          onClick={() => copyQuery(log.sentryQuery, log.id)}
                          className="text-purple-400 hover:text-purple-300 flex items-center gap-1 cursor-pointer"
                        >
                          {copiedId === log.id ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                          <span>{copiedId === log.id ? 'Tersalin' : 'Salin Search Query'}</span>
                        </button>
                      </div>
                    </div>
                  ))
              )}
            </div>
          </div>

          {/* Source Maps Verification Guide */}
          <div className="bg-indigo-50/80 border border-indigo-100 rounded-2xl p-5 space-y-3">
            <div className="flex items-center gap-2 text-indigo-900">
              <FileCode className="w-4 h-4 text-indigo-600" />
              <h3 className="text-xs font-bold uppercase tracking-wider">
                Panduan Verifikasi Pemetaan Source Maps
              </h3>
            </div>
            <p className="text-[11px] text-indigo-800 leading-relaxed">
              Setelah memicu error di atas, buka Dasbor Sentry.io untuk memeriksa stack trace:
            </p>
            <ul className="text-[11px] text-indigo-700 space-y-1.5 list-disc list-inside">
              <li>
                <strong>Source Maps Valid:</strong> Sentry menampilkan nama file TypeScript asli dan baris kode sebenarnya (contoh: <code>SentryDiagnosticPanel.tsx:84</code> atau <code>backend/app.ts:58</code>).
              </li>
              <li>
                <strong>Source Maps Belum Terpetakan:</strong> Sentry hanya menampilkan baris JavaScript yang terminifikasi (contoh: <code>assets/index-D1f3a.js:1:1420</code>).
              </li>
            </ul>
            <div className="pt-2">
              <a
                href="https://sentry.io/organizations/uibn/issues/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-[11px] font-bold text-indigo-700 hover:text-indigo-900 underline"
              >
                <span>Buka Dasbor Sentry (Org: uibn)</span>
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
